import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import type { FriendlyError } from '@/lib/errors';

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENV =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
  import.meta.env.MODE;
const RELEASE = import.meta.env.VITE_APP_VERSION as string | undefined;

export const sentryEnabled = Boolean(DSN);

export function initSentry() {
  if (!sentryEnabled) return;

  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      ...(import.meta.env.VITE_SENTRY_REPLAY === 'true'
        ? [
            Sentry.replayIntegration({
              maskAllText: true,
              maskAllInputs: true,
              blockAllMedia: true,
            }),
          ]
        : []),
    ],
    tracesSampleRate: ENV === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: true,
    beforeBreadcrumb(crumb) {
      if (crumb.category === 'console' && crumb.level !== 'error') return null;
      if (crumb.data && 'Authorization' in crumb.data) {
        delete (crumb.data as Record<string, unknown>).Authorization;
      }
      return crumb;
    },
  });
}

export function captureFriendlyError(
  err: unknown,
  friendly: FriendlyError,
  extra?: Record<string, unknown>
) {
  if (!sentryEnabled) return;
  if (friendly.category === 'validation') return;

  Sentry.withScope((scope) => {
    scope.setTag('error.category', friendly.category);
    scope.setTag('error.code', friendly.code);
    scope.setTag('error.retryable', String(friendly.retryable));
    scope.setLevel(levelForCategory(friendly.category));
    scope.setContext('friendly_error', { ...friendly });
    if (extra) scope.setContext('extra', extra);
    Sentry.captureException(
      err instanceof Error ? err : new Error(friendly.message)
    );
  });
}

function levelForCategory(c: FriendlyError['category']): Sentry.SeverityLevel {
  switch (c) {
    case 'rate_limit':
    case 'authentication':
    case 'account':
    case 'network':
      return 'warning';
    case 'server':
    case 'unknown':
    default:
      return 'error';
  }
}

export function setSentryUser(u: { id: string; email?: string } | null) {
  if (!sentryEnabled) return;
  Sentry.setUser(u ? { id: u.id, email: u.email } : null);
}

export { Sentry };
